export const SITE_URL = "https://strmonitoring.com";
export const SITE_NAME = "STR Monitoring";
export const CONTACT_EMAIL = "customerservice@strmonitoring.com";
export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;
export const DEFAULT_OG_IMAGE = `${SITE_URL}/images/og-default.jpg`;
export const LOGO_URL = `${SITE_URL}/images/logo.png`;

export type FaqItem = {
  question: string;
  answer: string;
};

export type BreadcrumbItem = {
  name: string;
  url: string;
};

export const isDefaultOgImage = (url: string): boolean =>
  url === DEFAULT_OG_IMAGE || url.endsWith("/images/og-default.jpg");

export const absoluteUrl = (path: string): string => {
  if (path.startsWith("http")) return path;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${normalized === "/" ? "/" : normalized}`;
};

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: LOGO_URL,
  email: CONTACT_EMAIL,
  contactPoint: {
    "@type": "ContactPoint",
    email: CONTACT_EMAIL,
    contactType: "customer service",
  },
});

export const webSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
  },
});

export const webPageSchema = ({
  name,
  description,
  url,
  image,
}: {
  name: string;
  description: string;
  url: string;
  image?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  name,
  description,
  url: absoluteUrl(url),
  ...(image
    ? { image: image.startsWith("http") ? image : absoluteUrl(image) }
    : {}),
  isPartOf: {
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
  },
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
  },
});

export const serviceSchema = ({
  name,
  description,
  audience,
  url,
  serviceType = "Short-term rental monitoring and compliance software",
  areaServedCountry = "United States",
}: {
  name: string;
  description: string;
  audience: string;
  url: string;
  serviceType?: string;
  areaServedCountry?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description,
  url: absoluteUrl(url),
  provider: {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: LOGO_URL,
    email: CONTACT_EMAIL,
  },
  areaServed: {
    "@type": "Country",
    name: areaServedCountry,
  },
  audience: {
    "@type": "Audience",
    audienceType: audience,
  },
  serviceType,
  category: "Short-term rental compliance monitoring",
});

export const howToSchema = ({
  name,
  description,
  steps,
  url,
}: {
  name: string;
  description: string;
  steps: string[];
  url: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name,
  description,
  url: absoluteUrl(url),
  step: steps.map((text, index) => ({
    "@type": "HowToStep",
    position: index + 1,
    name: `Step ${index + 1}`,
    text,
  })),
});

export const faqPageSchema = (faqs: FaqItem[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

export const breadcrumbSchema = (items: BreadcrumbItem[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.url),
  })),
});

export const htmlToPlainText = (html: string): string =>
  String(html || "")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();

export const extractFaqsFromHtml = (html: string): FaqItem[] => {
  const source = String(html || "");
  const faqs: FaqItem[] = [];
  const pattern =
    /<h3\b[^>]*>((?:(?!<\/h3>)[\s\S])*)<\/h3>\s*<p\b[^>]*>((?:(?!<\/p>)[\s\S])*)<\/p>/gi;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(source)) !== null) {
    const question = htmlToPlainText(match[1]);
    const answer = htmlToPlainText(match[2]);
    if (!question.endsWith("?") || !answer) continue;
    faqs.push({ question, answer });
  }
  return faqs;
};

export const wordCount = (text: string): number => {
  if (!text) return 0;
  return text.split(/\s+/).filter(Boolean).length;
};

export const blogPostingSchema = ({
  title,
  description,
  slug,
  coverUrl,
  authorName,
  datePublished,
  dateModified,
  articleHtml,
}: {
  title: string;
  description: string;
  slug: string;
  coverUrl?: string | null;
  authorName?: string | null;
  datePublished: string;
  dateModified?: string;
  articleHtml?: string | null;
}) => {
  const articleBody = htmlToPlainText(articleHtml || "");
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description,
    image: coverUrl || DEFAULT_OG_IMAGE,
    ...(articleBody
      ? {
          articleBody,
          wordCount: wordCount(articleBody),
        }
      : {}),
    author: {
      "@type": "Person",
      name: authorName || SITE_NAME,
      url: SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: {
        "@type": "ImageObject",
        url: LOGO_URL,
      },
    },
    datePublished,
    dateModified: dateModified || datePublished,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": absoluteUrl(`/blog/${slug}`),
    },
  };
};

export const blogSchema = ({
  description,
  posts,
}: {
  description: string;
  posts: Array<{
    title: string;
    description?: string | null;
    coverUrl?: string | null;
    slug: string;
    createdAt: string;
    authorName?: string | null;
  }>;
}) => ({
  "@context": "https://schema.org",
  "@type": "Blog",
  headline: "Latest Blog Posts",
  description,
  url: absoluteUrl("/blog"),
  blogPosts: posts.map((post) => ({
    "@type": "BlogPosting",
    headline: post.title,
    description: post.description,
    image: post.coverUrl,
    url: absoluteUrl(`/blog/${post.slug}`),
    datePublished: post.createdAt,
    author: {
      "@type": "Person",
      name: post.authorName || SITE_NAME,
    },
  })),
});
